import {
    type DashboardReference,
    withReferences
} from '@/lib/dashboardReferences';
import { getDashboard } from '@/lib/dashboards-api';
import { ArrowLeft, ArrowUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { toast } from 'sonner';
import useSWR from 'swr';

import { useChatInteract } from '@chainlit/react-client';

import AutoResizeTextarea from '@/components/AutoResizeTextarea';
import { DashboardFrame } from '@/components/Dashboards/DashboardFrame';
import { MobileNavTrigger } from '@/components/ScheduledAgents/MobileNavTrigger';
import ModelSelector from '@/components/chat/MessageComposer/DMModelSelector';
import { MultiAgentSelector } from '@/components/chat/MessageComposer/DMMultiAgentSelector';
import { DashboardReferences } from '@/components/chat/MessageComposer/DashboardReferences';
import { Button } from '@/components/ui/button';

import {
    composerModeState,
    dashboardReferencesState,
    pendingPromptState
} from '@/state/chat';
import { rightSidebarPanelState } from '@/state/project';

export function ArtifactDashboardView({
    dashboardId,
    backTo = '/artifacts',
    backLabel = 'Artifacts'
}: {
    dashboardId: string;
    backTo?: string;
    backLabel?: string;
}) {
    const navigate = useNavigate();
    const { clear } = useChatInteract();
    const [references, setReferences] = useRecoilState(
        dashboardReferencesState
    );
    const setActivePanel = useSetRecoilState(rightSidebarPanelState);
    const setPendingPrompt = useSetRecoilState(pendingPromptState);
    const composerMode = useRecoilValue(composerModeState);
    const [prompt, setPrompt] = useState('');
    const [sending, setSending] = useState(false);

    const { data: dashboard } = useSWR(
        ['dashboard', dashboardId],
        ([, id]) => getDashboard(id),
        { revalidateOnFocus: false, revalidateOnReconnect: false }
    );

    useEffect(() => {
        setReferences([]);
        return () => setReferences([]);
    }, [dashboardId, setReferences]);

    const sendPrompt = async (text: string, attached: DashboardReference[]) => {
        if (!dashboard || !text.trim() || sending) return;
        setSending(true);
        try {
            setPendingPrompt(withReferences(text.trim(), attached));
            setActivePanel('artifacts');
            window.dispatchEvent(new CustomEvent('newChat'));
            clear();
            navigate('/');
        } catch (sendError) {
            toast.error(
                sendError instanceof Error
                    ? `Could not send: ${sendError.message}`
                    : 'Could not send'
            );
            setSending(false);
        }
    };

    const send = () => sendPrompt(prompt, references);

    return (
        <div className="relative flex flex-col bg-background size-full min-h-0">
            <MobileNavTrigger />
            <div className="flex-1 min-h-0">
                <DashboardFrame
                    dashboardId={dashboardId}
                    onComment={(reference, text) =>
                        sendPrompt(text, [reference])
                    }
                    leading={
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => navigate(backTo)}
                            aria-label={`Back to ${backLabel}`}
                            title={`Back to ${backLabel}`}
                        >
                            <ArrowLeft />
                        </Button>
                    }
                />
            </div>
            {references.length > 0 && (
                <div className="bottom-4 left-1/2 z-40 absolute flex flex-col gap-2 bg-background shadow-xl p-3 border border-border rounded-2xl w-[min(720px,calc(100%-2rem))] animate-in duration-200 slide-in-from-bottom-4 -translate-x-1/2">
                    <DashboardReferences />
                    <div className="flex flex-col gap-2">
                        <AutoResizeTextarea
                            autoFocus
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            onKeyDown={(e) => {
                                if (
                                    e.key === 'Enter' &&
                                    !e.shiftKey &&
                                    !e.nativeEvent.isComposing
                                ) {
                                    e.preventDefault();
                                    send();
                                }
                            }}
                            placeholder="Ask the agent to change this dashboard..."
                            className="bg-transparent focus:outline-hidden w-full placeholder:text-muted-foreground resize-none text-base leading-6"
                            maxHeight={160}
                        />
                        <div className="flex justify-between items-center gap-2">
                            {composerMode === 'multiagent' ? (
                                <MultiAgentSelector />
                            ) : (
                                <ModelSelector />
                            )}
                            <Button
                                size="icon-sm"
                                onClick={send}
                                disabled={
                                    !dashboard || !prompt.trim() || sending
                                }
                                aria-label="Send"
                            >
                                <ArrowUp />
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
